import { BASE_URL } from 'constants/api';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '../constants/trial-annotation';

const TEACHER_FEEDBACK_ANALYSIS_ENDPOINT = '/teacher-feedback-analysis';

/**
 * Normalize API gateway value tuple into an object with payload and status code.
 * The gateway responds with either an array form `[payload, statusCode]` or a plain
 * payload object. This helper ensures downstream callers can rely on a consistent
 * `{ payload, statusCode }` structure.
 *
 * @param {(Array|Object|null)} result Raw `value` field returned by the gateway.
 * @returns {{ payload: any, statusCode: (number|undefined) }} Normalized payload data.
 */
const unwrapGatewayValue = result => {
  if (!result) {
    return { payload: null, statusCode: undefined };
  }

  if (Array.isArray(result)) {
    const [payload, statusCode] = result;
    return { payload, statusCode };
  }

  return { payload: result, statusCode: undefined };
};

/**
 * Base fetch utility with authentication headers
 * @param {string} endpoint - API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} - JSON response
 */
const fetchWithAuth = async (endpoint, options = {}) => {
  const response = await fetch(endpoint, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Build query parameters from params object
 * @param {Object} params - Parameters object
 * @param {number} params.page - Page number
 * @param {number} params.per_page - Items per page
 * @returns {URLSearchParams} - Query parameters
 */
const buildQueryParams = params => {
  const queryParams = new URLSearchParams();

  // Add pagination parameters with defaults
  queryParams.append('page', params.page || 1);
  queryParams.append('per_page', params.per_page || 20);

  // Add optional ID filters accepted by the gateway
  ['student_id', 'teacher_id'].forEach(key => {
    if (params[key]) {
      queryParams.append(key, params[key]);
    }
  });

  if (params.start_date !== undefined && params.start_date !== null) {
    queryParams.append('start_date', params.start_date);
  }
  if (params.end_date !== undefined && params.end_date !== null) {
    queryParams.append('end_date', params.end_date);
  }

  return queryParams;
};

/**
 * Fetch trial classrooms from gateway with pagination
 * @param {Object} params - Query parameters including page, per_page, and filters
 * @returns {Promise<Object>} - JSON response with data and pagination
 */
export const getTrialClassrooms = async (params = {}) => {
  const queryParams = buildQueryParams(params);
  return fetchWithAuth(`${BASE_URL}/classroom/trial-classrooms/?${queryParams}`);
};

/**
 * Get all annotations for a video
 * @param {string} videoId - Video ID (extracted from S3 URL)
 * @returns {Promise<Object>} - API response with annotations array
 */
export const getAnnotations = async videoId => {
  try {
    const response = await fetch(`${BASE_URL}/annotations/${videoId}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      credentials: 'include', // Include cookies for authentication
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Check for new structured error response format
    if (data.status === 'error' && data.error_code === 'ANNOTATION_NOT_FOUND') {
      throw new Error('Annotation not found');
    }

    return data;
  } catch (error) {
    console.error('Error fetching annotations:', error);
    throw error;
  }
};

/**
 * Create a new annotation
 * @param {string} videoId - Video ID (extracted from S3 URL)
 * @param {Object} annotationData - Annotation data to create
 * @returns {Promise<Object>} - API response with created annotation
 */
export const createAnnotation = async (videoId, annotationData) => {
  try {
    // Filter out any extra fields that shouldn't be in the request body
    const cleanAnnotationData = {
      content: annotationData.content,
      timestamp: annotationData.timestamp,
      reviewer_id: annotationData.reviewer_id,
      reviewer_name: annotationData.reviewer_name,
      emotion: annotationData.emotion,
      trial_part: annotationData.trial_part,
    };

    const response = await fetch(`${BASE_URL}/annotations/${videoId}/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      credentials: 'include',
      body: JSON.stringify(cleanAnnotationData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Check for new structured error response format
    if (data.status === 'error' && data.error_code === 'ANNOTATION_NOT_FOUND') {
      throw new Error('Annotation not found');
    }

    // Handle the response structure - successful create returns the annotation data
    if (data.value && data.value.status === 'success') {
      return {
        success: true,
        data: data.value.data || data.value,
      };
    } else {
      return data;
    }
  } catch (error) {
    console.error('Error creating annotation:', error);
    throw error;
  }
};

/**
 * Update an existing annotation
 * @param {string} videoId - Video ID (extracted from S3 URL)
 * @param {string} annotationId - Annotation ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} - API response with updated annotation
 */
export const updateAnnotation = async (videoId, annotationId, updateData) => {
  try {
    const response = await fetch(`${BASE_URL}/annotations/${videoId}/${annotationId}/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      credentials: 'include',
      body: JSON.stringify(updateData),
    });

    const data = await response.json();

    // Check for new structured error response format
    if (data.status === 'error' && data.error_code === 'ANNOTATION_NOT_FOUND') {
      throw new Error('Annotation not found');
    }

    // Handle the response structure - successful update returns the updated annotation data
    if (response.ok) {
      if (data.value && data.value.status === 'success') {
        return {
          success: true,
          data: data.value.data || data.value,
        };
      } else if (data.success) {
        return data;
      } else {
        return {
          success: true,
          data: data,
        };
      }
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Error updating annotation:', error);
    throw error;
  }
};

/**
 * Delete an annotation
 * @param {string} videoId - Video ID (extracted from S3 URL)
 * @param {string} annotationId - Annotation ID
 * @returns {Promise<Object>} - API response
 */
export const deleteAnnotation = async (videoId, annotationId) => {
  try {
    const response = await fetch(`${BASE_URL}/annotations/${videoId}/${annotationId}/`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Check for new structured error response format
    if (data.status === 'error' && data.error_code === 'ANNOTATION_NOT_FOUND') {
      throw new Error('Annotation not found');
    }

    // Handle the response structure - successful delete returns success message
    if (data.value && data.value.status === 'success') {
      return {
        success: true,
        message: data.value.message || 'Annotation deleted successfully',
      };
    } else {
      return data;
    }
  } catch (error) {
    console.error('Error deleting annotation:', error);
    throw error;
  }
};

/**
 * Extract video ID from S3 URL
 * @param {string} s3Url - S3 URL like https://s3-ap-southeast-1.amazonaws.com/cuemath-tokbox/.../22dcb41f-c10b-46c2-9b01-de9e192cd14d/archive.mp4
 * @returns {string} - Video ID (UUID before archive.mp4)
 */
export const extractVideoIdFromS3Url = s3Url => {
  const match = s3Url.match(/\/([a-f0-9-]{36})\/archive\.mp4$/i);
  if (match && match[1]) {
    return match[1];
  }
  throw new Error(`Invalid S3 URL format: ${s3Url}`);
};

/**
 * Get transcript for a video
 * @param {string} videoId - The video UUID to fetch transcript for
 * @returns {Promise<Object>} - Transcript data with video_id, transcript array, and status
 */
export const getTranscript = async videoId => {
  try {
    const response = await fetch(
      `${BASE_URL}/video-transcription/transcript/${videoId}/`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(ERROR_MESSAGES.TRANSCRIPT.FETCH_FAILED);
    }

    const data = await response.json();

    // Check if transcript actually exists in the response body
    if (data.value && data.value.status === 'not_found') {
      throw new Error(ERROR_MESSAGES.TRANSCRIPT.NOT_FOUND);
    }

    // Check for new structured error response format
    if (data.status === 'error' && data.error_code === 'TRANSCRIPT_NOT_FOUND') {
      throw new Error(ERROR_MESSAGES.TRANSCRIPT.NOT_FOUND);
    }

    return data;
  } catch (error) {
    console.error('Error fetching transcript:', error);
    throw error;
  }
};

/**
 * Get transcript via the Sonus-backed gateway endpoint, keyed by object_id
 * (the teacher_classroom_id today). Returns the same wire shape as
 * `getTranscript` so callers can branch on `data.value.status` uniformly.
 * @param {string} objectId - The classroom UUID submitted to Sonus
 * @returns {Promise<Object>} - Transcript data in the same shape getTranscript returns
 */
export const getTranscriptByObjectId = async objectId => {
  const response = await fetch(`${BASE_URL}/transcripts/${objectId}/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
    },
  });

  if (!response.ok) {
    // Gateway returns 404 when Sonus has nothing for this object_id —
    // signal NOT_FOUND so the caller can fall back to the legacy path.
    throw new Error(ERROR_MESSAGES.TRANSCRIPT.NOT_FOUND);
  }

  return response.json();
};

/**
 * Check transcript status (for polling) - does NOT trigger creation.
 *
 * Tries the Sonus-backed path first (full stitched transcript covering
 * all archives for the class). On NOT_FOUND, falls through to the legacy
 * DeepGram/DynamoDB lookup keyed on the first archive's videoId — which
 * is the exact behaviour callers had before this composer was added.
 *
 * @param {{objectId?: string, videoId: string}} ids - object_id (=teacher_classroom_id)
 *   for the Sonus path; videoId for the legacy fallback. videoId stays required
 *   for the fallback to keep working until Sonus covers all in-flight classes.
 * @returns {Promise<Object>} - { exists, data, processing, message?, error? }
 */
export const checkTranscriptStatus = async ({ objectId, videoId }) => {
  if (objectId) {
    try {
      const transcript = await getTranscriptByObjectId(objectId);
      if (transcript.value && transcript.value.status === 'success') {
        return { exists: true, data: transcript, processing: false };
      }
      // Unrecognised status from the new path — fall through to legacy
      // rather than treating as a hard error.
    } catch (error) {
      // Only NOT_FOUND falls through; other errors (network, 500) bubble.
      if (error.message !== ERROR_MESSAGES.TRANSCRIPT.NOT_FOUND) {
        return {
          exists: false,
          processing: false,
          error: error.message || ERROR_MESSAGES.TRANSCRIPT.FETCH_FAILED,
        };
      }
    }
  }

  return checkTranscriptStatusLegacy(videoId);
};

const checkTranscriptStatusLegacy = async videoId => {
  try {
    const transcript = await getTranscript(videoId);
    if (transcript.value && transcript.value.status === 'success') {
      return { exists: true, data: transcript, processing: false };
    }
    return {
      exists: false,
      processing: false,
      error: ERROR_MESSAGES.TRANSCRIPT.FETCH_FAILED,
    };
  } catch (error) {
    return {
      exists: false,
      processing: false,
      error: error.message || ERROR_MESSAGES.TRANSCRIPT.FETCH_FAILED,
    };
  }
};

export const updateTrialAnnotationStatus = async (teacherClassroomId, annotatorName) => {
  try {
    const response = await fetch(
      `${BASE_URL}/insightsreader/trials/${teacherClassroomId}/annotation/`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          annotation_status: 'Annotated',
          annotator_name: annotatorName,
        }),
      },
    );

    const data = await response.json();

    if (response.ok && data.success) {
      return {
        success: true,
        data: data.data,
      };
    } else {
      // Handle new error format with 'message' field (fallback to 'error' for compatibility)
      throw new Error(
        data.message || data.error || `HTTP error! status: ${response.status}`,
      );
    }
  } catch (error) {
    console.error('Error updating trial annotation status:', error);
    throw error;
  }
};

/**
 * Trigger Sales Pitch generation for a classroom.
 *
 * Sends the Sonus-produced utterances the FE has already loaded into
 * the trial-analysis transcript state. The gateway forwards them
 * straight to the Lambda — no upstream transcription kicked off here,
 * no DynamoDB poll on the Lambda side
 * (see cuemath/inteladmingateway#357).
 *
 * @param {string} classroomId - The classroom UUID
 * @param {Array<Object>} utterances - Sonus utterances; each has
 *   { speaker_id, start_time, end_time, transcript, confidence }.
 * @returns {Promise<Object>} - Response with status and message
 */
export const triggerSalesPitch = async (classroomId, utterances) => {
  const requestBody = {
    classroom_id: classroomId,
    utterances,
  };

  try {
    const response = await fetch(`${BASE_URL}/post-trial-sales-pitch/trigger/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error triggering sales pitch:', error);
    throw error;
  }
};

/**
 * Get Sales Pitch for a classroom
 * @param {string} classroomId - The classroom UUID
 * @returns {Promise<Object>} - Sales pitch data with classroom_id and sales_pitch
 */
export const getSalesPitch = async classroomId => {
  try {
    const response = await fetch(
      `${BASE_URL}/post-trial-sales-pitch/sales-pitch/${classroomId}/`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Check for new structured error response format
    const errorData = data.value?.[0];
    if (
      errorData?.status === 'error' &&
      errorData?.error_code === 'SALES_PITCH_NOT_FOUND'
    ) {
      return {
        status: 'not_found',
        message: errorData.message || 'Sales pitch not found for this classroom',
      };
    }

    return data;
  } catch (error) {
    console.error('Error fetching sales pitch:', error);
    throw error;
  }
};

/**
 * Check Sales Pitch status (for polling) - does NOT trigger creation
 * @param {string} classroomId - The classroom UUID
 * @returns {Promise<Object>} - Object with exists, data, processing, and message properties
 */
export const checkSalesPitchStatus = async classroomId => {
  try {
    const salesPitch = await getSalesPitch(classroomId);

    // Handle API response structure: check for nested error in value array
    if (salesPitch.status === 'not_found') {
      return {
        exists: false,
        processing: false,
        error: salesPitch.message || 'Sales pitch not found',
      };
    }

    // Handle nested response structure: data is in salesPitch.value
    const responseData = salesPitch.value || salesPitch;

    // Check if sales pitch is completed
    if (responseData.status === 'success') {
      return {
        exists: true,
        data: salesPitch,
        processing: false,
      };
    } else if (responseData.status === 'not_found') {
      return {
        exists: false,
        processing: false,
        error: responseData.message || 'Sales pitch not found',
      };
    } else {
      // Unexpected status
      return {
        exists: false,
        processing: false,
        error: responseData.message || 'Sales pitch not available',
      };
    }
  } catch (error) {
    return {
      exists: false,
      processing: false,
      error: error.message || 'Failed to check sales pitch status',
    };
  }
};

/**
 * Get Sales Pitch or trigger creation if it doesn't exist.
 *
 * @param {string} classroomId - The classroom UUID
 * @param {Array<Object>} utterances - Sonus utterances forwarded to the
 *   trigger endpoint when no sales pitch exists yet.
 * @returns {Promise<Object>} - Object with exists, data, processing, and message properties
 */
export const getOrCreateSalesPitch = async (classroomId, utterances) => {
  // Validate inputs
  if (!classroomId || !utterances || utterances.length === 0) {
    return {
      exists: false,
      processing: false,
      error: 'Missing required parameters: classroomId and utterances',
    };
  }

  // Step 1: Try to get existing sales pitch
  try {
    const salesPitch = await getSalesPitch(classroomId);

    // Handle nested error response structure
    if (salesPitch.status === 'not_found') {
      // Sales pitch not found, trigger creation
      // Step 2: Trigger sales pitch creation
      try {
        const triggerResult = await triggerSalesPitch(classroomId, utterances);
        const triggerData = triggerResult.value || triggerResult;

        if (triggerData.status === 'success') {
          if (triggerData.skipped) {
            // Already exists, fetch it directly
            const existingSalesPitch = await getSalesPitch(classroomId);
            return {
              exists: true,
              data: existingSalesPitch,
              processing: false,
            };
          } else {
            // Successfully triggered generation
            return {
              exists: false,
              processing: true,
              message:
                triggerData.message || 'Sales pitch generation triggered successfully',
            };
          }
        } else {
          throw new Error(
            triggerData.message || 'Failed to trigger sales pitch generation',
          );
        }
      } catch (triggerError) {
        return {
          exists: false,
          processing: false,
          error: triggerError.message || 'Failed to trigger sales pitch generation',
        };
      }
    }

    const responseData = salesPitch.value || salesPitch;

    // Check if sales pitch is completed
    if (responseData.status === 'success') {
      return {
        exists: true,
        data: salesPitch,
        processing: false,
      };
    } else {
      // Unexpected status
      throw new Error(responseData.message || 'Unexpected sales pitch status');
    }
  } catch (error) {
    // Other error
    return {
      exists: false,
      processing: false,
      error: error.message || 'Failed to fetch sales pitch',
    };
  }
};

/**
 * Retrieve AI analysis document for a classroom.
 * Handles gateway-wrapped responses and returns the merged value + status code.
 *
 * @param {string} classroomId - Classroom UUID whose analysis we need.
 * @returns {Promise<{ value: any, statusCode: number }>} Normalised response payload.
 * @throws {Error} When the fetch fails for non-404 responses.
 */
export const getAIAnalysis = async classroomId => {
  try {
    const response = await fetch(
      `${BASE_URL}${TEACHER_FEEDBACK_ANALYSIS_ENDPOINT}/analysis/${classroomId}/`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      },
    );

    const data = await response.json();

    const { payload, statusCode } = unwrapGatewayValue(data.value);
    const merged = {
      ...data,
      value: payload,
      statusCode: statusCode ?? response.status,
    };

    if (!response.ok && response.status !== 404) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return merged;
  } catch (error) {
    console.error('[AIAnalysis][GET] error', error);
    throw error;
  }
};

/**
 * Trigger AI analysis generation for a classroom.
 * Gateway may respond with a wrapped payload; the helper normalises it for callers.
 *
 * Sends the Sonus-produced utterances the FE has already loaded into
 * the trial-analysis transcript state. The gateway forwards them
 * straight to the Lambda — no upstream transcription kicked off here
 * (see cuemath/inteladmingateway#357).
 *
 * @param {string} classroomId - Classroom UUID.
 * @param {Array<Object>} utterances - Sonus utterances; each has
 *   { speaker_id, start_time, end_time, transcript, confidence }.
 * @returns {Promise<{ value: any, statusCode: number }>} Trigger response with status metadata.
 * @throws {Error} When the trigger endpoint returns a non-OK status.
 */
export const triggerAIAnalysis = async (classroomId, utterances) => {
  try {
    const response = await fetch(
      `${BASE_URL}${TEACHER_FEEDBACK_ANALYSIS_ENDPOINT}/trigger/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          classroom_id: classroomId,
          utterances,
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const { payload, statusCode } = unwrapGatewayValue(data.value ?? data);
    const merged = {
      ...data,
      value: payload,
      statusCode: statusCode ?? response.status,
    };
    return merged;
  } catch (error) {
    console.error('[AIAnalysis][POST trigger] error', error);
    throw error;
  }
};

/**
 * Poll helper that inspects the AI analysis status for a classroom.
 *
 * @param {string} classroomId - Classroom UUID.
 * @returns {Promise<{
 *   exists: boolean,
 *   processing: boolean,
 *   notFound?: boolean,
 *   data?: any,
 *   message?: string,
 *   error?: string
 * }>} Status descriptor used by polling loops.
 */
export const checkAIAnalysisStatus = async classroomId => {
  try {
    const analysis = await getAIAnalysis(classroomId);
    const payload = analysis.value ?? analysis;
    const statusCode = analysis.statusCode;
    const status = payload?.status;

    if (
      (status === 'error' &&
        payload?.error_code === 'TEACHER_FEEDBACK_ANALYSIS_NOT_FOUND') ||
      statusCode === 404
    ) {
      return {
        exists: false,
        processing: false,
        notFound: true,
      };
    }

    if (status === 'success') {
      return {
        exists: true,
        data: payload,
        processing: false,
      };
    }

    if (status === 'pending') {
      return {
        exists: true,
        data: payload,
        processing: true,
        message: payload?.message,
      };
    }

    return {
      exists: false,
      processing: false,
      error: payload?.message || ERROR_MESSAGES.AI_ANALYSIS.FETCH_FAILED,
      message: payload?.message,
    };
  } catch (error) {
    return {
      exists: false,
      processing: false,
      error: error.message || 'Failed to check AI analysis status',
    };
  }
};

/**
 * Convenience helper that fetches AI analysis or triggers generation when absent.
 *
 * @param {string} classroomId - Classroom UUID.
 * @param {Array<Object>} utterances - Sonus utterances forwarded to the
 *   trigger endpoint when no analysis exists yet.
 * @returns {Promise<{
 *   exists: boolean,
 *   processing: boolean,
 *   data?: any,
 *   message?: string,
 *   error?: string
 * }>} Result payload summarising current state.
 */
export const getOrCreateAIAnalysis = async (classroomId, utterances) => {
  if (!classroomId || !utterances || utterances.length === 0) {
    return {
      exists: false,
      processing: false,
      error: 'Missing required parameters: classroomId and utterances',
    };
  }

  try {
    const analysis = await getAIAnalysis(classroomId);

    const payload = analysis.value ?? analysis;
    const statusCode = analysis.statusCode;
    const status = payload?.status;

    if (status === 'success') {
      return {
        exists: true,
        data: payload,
        processing: false,
      };
    }

    const isNotFound =
      (status === 'error' &&
        payload?.error_code === 'TEACHER_FEEDBACK_ANALYSIS_NOT_FOUND') ||
      statusCode === 404;

    if (isNotFound) {
      try {
        const triggerResult = await triggerAIAnalysis(classroomId, utterances);
        const triggerData = triggerResult.value || triggerResult;

        if (triggerData.status === 'success' && triggerData.skipped) {
          const existingAnalysis = await getAIAnalysis(classroomId);
          const existingPayload = existingAnalysis.value ?? existingAnalysis;
          if (existingPayload?.status === 'success') {
            return {
              exists: true,
              data: existingPayload,
              processing: false,
            };
          }
        }

        if (triggerData.status === 'success') {
          return {
            exists: false,
            processing: true,
            message: triggerData.message || SUCCESS_MESSAGES.AI_ANALYSIS.POLLING,
          };
        }

        throw new Error(triggerData.message || ERROR_MESSAGES.AI_ANALYSIS.TRIGGER_FAILED);
      } catch (triggerError) {
        return {
          exists: false,
          processing: false,
          error: triggerError.message || ERROR_MESSAGES.AI_ANALYSIS.TRIGGER_FAILED,
        };
      }
    }

    return {
      exists: false,
      processing: false,
      error: payload?.message || ERROR_MESSAGES.AI_ANALYSIS.FETCH_FAILED,
    };
  } catch (error) {
    return {
      exists: false,
      processing: false,
      error: error.message || ERROR_MESSAGES.AI_ANALYSIS.FETCH_FAILED,
    };
  }
};
